import React from 'react';
import { Typography, Paper, Button, Grid, Box, Chip, Tooltip } from '@material-ui/core';
import {
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  EmojiEvents as AwardIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  Error as ErrorIcon,
} from '@material-ui/icons';

const Resumo = ({ formData, getDistributorNames, handleSubmit, createPlanStatus, handleCancel, classes }) => {
  const formatCurrency = value => {
    if (!value && value !== 0) {
      return 'R$ 0,00';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(numValue)) {
      return 'R$ 0,00';
    }
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPercent = value => {
    if (!value && value !== 0) {
      return '0%';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(numValue)) {
      return '0%';
    }
    return `${numValue.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  };

  const formatPenaltyReason = reason => {
    const reasons = {
      non_payment: 'Inadimplência',
      churn: 'Cancelamento',
    };
    return reasons[reason] || reason;
  };

  const distributorNames = getDistributorNames();
  const hasBonus = formData.bonuses.length > 0;
  const hasPenalties = formData.penalties.length > 0;
  const hasPayment =
    formData.payment.hasInitialPayment || formData.payment.hasInvoicePayment || formData.payment.hasRecurrence;

  // Validação dos campos obrigatórios
  const validation = {
    name: !formData.name || formData.name.trim() === '',
    distributors: distributorNames.length === 0,
    calculation:
      formData.calculationType === 'fixed'
        ? !formData.fixedValue && formData.fixedValue !== 0
        : formData.scales.length === 0 || formData.scales.every(s => !s.minValue && !s.maxValue && !s.percent),
  };

  const missingFields = [];
  if (validation.name) {
    missingFields.push('Nome do Plano');
  }
  if (validation.distributors) {
    missingFields.push('Distribuidoras');
  }
  if (validation.calculation) {
    missingFields.push('Cálculo de Comissionamento');
  }

  const isFormValid = missingFields.length === 0;

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid #e0e0e0',
  };

  const infoRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '10px 0',
    borderBottom: '1px solid #f5f5f5',
  };

  const itemCardStyle = {
    padding: 12,
    backgroundColor: '#fafafa',
    marginBottom: 8,
    borderRadius: 4,
  };

  const errorStyle = {
    color: '#c62828',
    fontWeight: 600,
  };

  const errorLabelStyle = {
    color: '#c62828',
  };

  return (
    <div className={classes.sectionGap}>
      <div style={{ marginBottom: 24 }}>
        <Typography variant="h6" gutterBottom>
          Resumo do Plano
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Revise todas as configurações antes de salvar
        </Typography>
        {!isFormValid && (
          <Paper
            style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: '#ffebee',
              border: '1px solid #ef9a9a',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <ErrorIcon style={{ color: '#c62828' }} />
            <div>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#c62828' }}>
                Campos obrigatórios não preenchidos:
              </Typography>
              <Typography variant="body2" style={{ color: '#c62828' }}>
                {missingFields.join(', ')}
              </Typography>
            </div>
          </Paper>
        )}
      </div>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            className={classes.configSection}
            style={{
              height: '100%',
              border: validation.name || validation.distributors ? '2px solid #ef9a9a' : undefined,
            }}
          >
            <div style={sectionHeaderStyle}>
              <InfoIcon style={{ color: validation.name || validation.distributors ? '#c62828' : '#1976d2' }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Informações Básicas
              </Typography>
              {(validation.name || validation.distributors) && (
                <Chip
                  size="small"
                  label="Incompleto"
                  style={{ marginLeft: 'auto', backgroundColor: '#ffebee', color: '#c62828', fontSize: 11 }}
                />
              )}
            </div>
            <div style={infoRowStyle}>
              <Typography variant="body2" style={validation.name ? errorLabelStyle : { color: 'textSecondary' }}>
                Nome do Plano {validation.name && '*'}
              </Typography>
              <Typography variant="body2" style={validation.name ? errorStyle : { fontWeight: 600 }}>
                {formData.name || 'Não informado'}
              </Typography>
            </div>
            <div style={{ ...infoRowStyle, borderBottom: 'none' }}>
              <Typography
                variant="body2"
                style={validation.distributors ? errorLabelStyle : { color: 'textSecondary' }}
              >
                Distribuidoras {validation.distributors && '*'}
              </Typography>
              <Box style={{ textAlign: 'right', maxWidth: '60%' }}>
                {distributorNames.length > 0 ? (
                  <Box style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 4 }}>
                    {distributorNames.map(name => (
                      <Chip
                        key={name}
                        label={name}
                        size="small"
                        style={{ fontSize: 11, height: 24, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" style={errorStyle}>
                    Nenhuma selecionada
                  </Typography>
                )}
              </Box>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            className={classes.configSection}
            style={{
              height: '100%',
              border: validation.calculation ? '2px solid #ef9a9a' : undefined,
            }}
          >
            <div style={sectionHeaderStyle}>
              <MoneyIcon style={{ color: validation.calculation ? '#c62828' : '#5c6bc0' }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Cálculo de Comissionamento
              </Typography>
              {validation.calculation ? (
                <Chip
                  size="small"
                  label="Incompleto"
                  style={{ marginLeft: 'auto', backgroundColor: '#ffebee', color: '#c62828', fontSize: 11 }}
                />
              ) : (
                <Chip
                  label={formData.calculationType === 'fixed' ? 'Valor Fixo' : 'Escalonado'}
                  size="small"
                  style={{ marginLeft: 'auto', backgroundColor: '#5c6bc0', color: '#fff', fontSize: 11 }}
                />
              )}
            </div>
            {formData.calculationType === 'fixed' ? (
              <div style={infoRowStyle}>
                <Typography
                  variant="body2"
                  style={validation.calculation ? errorLabelStyle : { color: 'textSecondary' }}
                >
                  {formData.fixedType === 'fixed' ? 'Valor Fixo' : 'Percentual'} {validation.calculation && '*'}
                </Typography>
                <Typography
                  variant="body1"
                  style={validation.calculation ? errorStyle : { fontWeight: 600, color: '#5c6bc0' }}
                >
                  {(() => {
                    if (!formData.fixedValue) {
                      return 'Não informado';
                    }
                    if (formData.fixedType === 'fixed') {
                      return formatCurrency(formData.fixedValue);
                    }
                    return formatPercent(formData.fixedValue);
                  })()}
                </Typography>
              </div>
            ) : (
              <div>
                <Typography
                  variant="body2"
                  style={{
                    marginBottom: 8,
                    ...(validation.calculation ? errorLabelStyle : { color: 'textSecondary' }),
                  }}
                >
                  Faixas de Comissão ({formData.scales.length}) {validation.calculation && '*'}
                </Typography>
                {formData.scales.length > 0 ? (
                  formData.scales.map((scale, index) => (
                    <Paper key={scale.id || `summary-scale-${index}`} variant="outlined" style={itemCardStyle}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          {scale.minValue || 0} - {scale.maxValue || '∞'} MWh
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#5c6bc0' }}>
                          {scale.percent ? `${scale.percent}%` : '-'}
                        </Typography>
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" style={errorStyle}>
                    Nenhuma faixa configurada
                  </Typography>
                )}
              </div>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper className={classes.configSection} style={{ height: '100%' }}>
            <div style={sectionHeaderStyle}>
              <PaymentIcon style={{ color: '#2e7d32' }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Pagamento
              </Typography>
              {hasPayment && (
                <Chip
                  label="Configurado"
                  size="small"
                  style={{ marginLeft: 'auto', backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: 11 }}
                />
              )}
            </div>
            {hasPayment ? (
              <>
                {formData.payment.hasInitialPayment && (
                  <Paper variant="outlined" style={itemCardStyle}>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Pagamento Inicial
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#2e7d32' }}>
                        {formData.payment.initialPaymentType === 'fixed'
                          ? formatCurrency(formData.payment.initialPaymentValue)
                          : formatPercent(formData.payment.initialPaymentValue)}
                      </Typography>
                    </Box>
                  </Paper>
                )}
                {formData.payment.hasInvoicePayment && (
                  <Paper variant="outlined" style={itemCardStyle}>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Pagamento de Primeira Fatura
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#2e7d32' }}>
                        {formData.payment.invoicePaymentType === 'fixed'
                          ? formatCurrency(formData.payment.invoicePaymentValue)
                          : formatPercent(formData.payment.invoicePaymentValue)}
                      </Typography>
                    </Box>
                  </Paper>
                )}
                {formData.payment.hasRecurrence && (
                  <Paper variant="outlined" style={itemCardStyle}>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        Recorrência Mensal
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#2e7d32' }}>
                        {formData.payment.recurrenceType === 'fixed'
                          ? formatCurrency(formData.payment.recurrenceValue)
                          : formatPercent(formData.payment.recurrenceValue)}
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </>
            ) : (
              <div className={classes.emptyState} style={{ padding: 24 }}>
                <Typography variant="body2" color="textSecondary">
                  Nenhum pagamento configurado
                </Typography>
              </div>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper className={classes.configSection} style={{ height: '100%' }}>
            <div style={sectionHeaderStyle}>
              <AwardIcon style={{ color: '#FF5246' }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Bônus
              </Typography>
              {hasBonus && (
                <Chip
                  label={`${formData.bonuses.length}`}
                  size="small"
                  style={{ marginLeft: 'auto', backgroundColor: '#fff3e0', color: '#e65100', fontSize: 11 }}
                />
              )}
            </div>
            {hasBonus ? (
              formData.bonuses.map((bonus, index) => (
                <Paper
                  key={bonus.aggregateBonusId ? `bonus-${bonus.aggregateBonusId}-${index}` : `bonus-solo-${index}`}
                  variant="outlined"
                  style={itemCardStyle}
                >
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Meta: <strong>{bonus.bonusTrigger || '-'}</strong>
                      {bonus.condition && ` (${bonus.condition})`}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#FF5246' }}>
                      {bonus.value ? formatCurrency(bonus.value) : '-'}
                    </Typography>
                  </Box>
                </Paper>
              ))
            ) : (
              <div className={classes.emptyState} style={{ padding: 24 }}>
                <Typography variant="body2" color="textSecondary">
                  Nenhum bônus configurado
                </Typography>
              </div>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper className={classes.configSection}>
            <div style={sectionHeaderStyle}>
              <WarningIcon style={{ color: '#c62828' }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Penalidades
              </Typography>
              {hasPenalties && (
                <Chip
                  label={`${formData.penalties.length}`}
                  size="small"
                  style={{ marginLeft: 'auto', backgroundColor: '#ffebee', color: '#c62828', fontSize: 11 }}
                />
              )}
            </div>
            {hasPenalties ? (
              <Grid container spacing={2}>
                {formData.penalties.map((penalty, index) => (
                  <Grid item xs={12} sm={6} md={4} key={penalty.id || `summary-penalty-${index}`}>
                    <Paper variant="outlined" style={{ ...itemCardStyle, marginBottom: 0 }}>
                      <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                        {penalty.name || formatPenaltyReason(penalty.type) || `Penalidade ${index + 1}`}
                      </Typography>
                      {penalty.value && (
                        <Typography variant="body1" style={{ fontWeight: 600, color: '#c62828' }}>
                          {penalty.unit === 'fixed' ? formatCurrency(penalty.value) : formatPercent(penalty.value)}
                        </Typography>
                      )}
                      {penalty.type && (
                        <Typography variant="caption" color="textSecondary">
                          {formatPenaltyReason(penalty.type)}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <div className={classes.emptyState} style={{ padding: 24 }}>
                <Typography variant="body2" color="textSecondary">
                  Nenhuma penalidade configurada
                </Typography>
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>

      <div style={{ marginTop: 32, display: 'flex', justifyContent: 'flex-end', gap: 16, alignItems: 'center' }}>
        <Button variant="outlined" onClick={handleCancel} disabled={createPlanStatus === 'loading'}>
          Cancelar
        </Button>
        <Tooltip
          title={!isFormValid ? `Preencha os campos obrigatórios: ${missingFields.join(', ')}` : ''}
          arrow
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              disabled={createPlanStatus === 'loading' || !isFormValid}
              onClick={handleSubmit}
              style={{ minWidth: 280 }}
            >
              {createPlanStatus === 'loading' ? 'Salvando...' : 'Salvar Plano de Comissionamento'}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default Resumo;
